import { Component, Element, Prop, h, State, Listen, Host } from '@stencil/core';
import store from '../sew-ws-header/store/store';

@Component({
    tag: 'sew-ws-protected-link',
    styleUrl: 'sew-ws-protected-link.scss',
    shadow: true,
})
export class SewWsProtectedLink {
    @Element() public el: HTMLSewWsProtectedLinkElement;
    /**
     * State of button.
     */
    @Prop({ reflect: true }) type: 'default' | 'external' | 'anchor' | 'download' = 'default';
    @Prop({ reflect: true }) size: 'default' | 'large' | 'xl' | 'bold' = 'default';
    @Prop() loginRequired: boolean = false;
    @Prop() authorizationRequired: boolean = false;
    @Prop() hideWhenLoggedIn: boolean = false;
    @Prop() roleRequired?: string;
    @Prop() level: number = 0;
    @Prop() os: boolean = true;

    @State() language: string;
    @State() country: string;
    @State() user: any;
    @State() roles: { auth: string; status: string }[];
    @State() isVisible = false;

    @Listen('languageSwitch', { target: 'window' })
    languageSwitchHandler(event) {
        this.language = event.detail;
    }

    @Listen('countryShort', { target: 'window' })
    countryShortHandler(event) {
        this.country = event.detail;
    }

    @Listen('userChange', { target: 'window' })
    userChangeHandler(event) {
        this.user = event.detail;
    }

    @Listen('permissionChange', { target: 'window' })
    permissionChangeHandler(event) {
        this.roles = event.detail;
    }

    @State() shouldDisplayLockIcon = true;

    private loggedIn: boolean;
    private hasRole: boolean;

    private previousLink: string;

    private handleProtectionChecks = () => {
        if (
            this.loggedIn === undefined ||
            this.hasRole === undefined ||
            this.user === undefined ||
            this.roles === undefined
        ) {
            return;
        }

        if (this.loginRequired === false) {
            this.isVisible = true;
            this.shouldDisplayLockIcon = false;
            return;
        }

        if (this.authorizationRequired === true && this.checkIfUserIsAuthorized() === false) {
            return;
        }

        if (this.loggedIn !== true) {
            this.isVisible = true;
            const anchor: HTMLAnchorElement = this.el.getElementsByTagName('a')[0];

            if (this.previousLink === null || this.previousLink === undefined) {
                this.previousLink = anchor.getAttribute('href');
            }

            anchor.href = `/os/c/main.do?viewset=LOGIN&LANGUAGE=${this.language}&COUNTRY=${
                this.country
            }&ONSUCCESS=${encodeURIComponent(this.previousLink)}`;

            return;
        }

        if (this.hideWhenLoggedIn === true) {
            return;
        }

        if (this.roleRequired === null || this.roleRequired === undefined) {
            this.shouldDisplayLockIcon = false;
            this.isVisible = true;
            return;
        }

        if (this.hasRole !== true) {
            this.isVisible = true;
            const anchor: HTMLAnchorElement = this.el.getElementsByTagName('a')[0];

            if (this.previousLink === null || this.previousLink === undefined) {
                this.previousLink = anchor.getAttribute('href');
            }

            anchor.href = `/os/c/main.do?ADDON=USER&JSLOAD=X&PAGE=APPLICATION_REQUEST&AUTH=${
                this.roleRequired
            }&LANGUAGE=${this.language}&COUNTRY=${this.country}&ONSUCCESS=${encodeURIComponent(
                this.previousLink
            )}`;

            return;
        }
        this.isVisible = true;
        this.shouldDisplayLockIcon = false;
    };

    private checkIfUserHasRole = () => {
        if (this.roles !== undefined && this.roles !== null) {
            this.hasRole = this.roles.some(role => role.auth === this.roleRequired);
        } else {
            this.hasRole = false;
        }
    };

    private checkIfUserIsLoggedIn = () => {
        if (this.user !== undefined && this.user !== null) {
            this.loggedIn = true;
        } else {
            this.loggedIn = false;
        }
    };

    private checkIfUserIsAuthorized = () => {
        if (this.loggedIn === true && this.hasRole === true) {
            return true;
        } else {
            return false;
        }
    };

    componentWillRender() {
        // we usually get all the needed data from the respective events
        // for links that are rendered too late the events have already been fired
        // in that case we get the necessary data from the store
        if (this.user === undefined) {
            this.user = store.state.user;
        }
        if (this.country === undefined && store.state.domainInfo) {
            this.country = store.state.domainInfo.country;
        }
        if (this.roles === undefined) {
            this.roles = store.state.permissions;
        }
        if (this.language === undefined) {
            this.language = store.state.language;
        }
        this.checkIfUserIsLoggedIn();
        this.checkIfUserHasRole();

        this.handleProtectionChecks();
    }

    render() {
        return (
            <Host class={{ hide: !this.isVisible }}>
                {this.isVisible ? (
                    <sew-ws-link
                        os={this.os ? true : null}
                        type={this.shouldDisplayLockIcon === true ? 'protected' : 'default'}
                    >
                        <slot></slot>
                    </sew-ws-link>
                ) : null}
            </Host>
        );
    }
}
